import { inject, Injectable } from '@angular/core';
import {
  AUTH_CONTEXT,
  AuthContextId,
  CustomerIdentityAuthenticationMethod,
  CustomerIdentityMfaPhones,
} from '@ld.consumer-portal/models';
import { CiamAuthService, CiamCallbackResult } from './ciam-auth.service';
import { CustomerIdentityService } from './customer-identity.service';
import { LoggingService } from './logging.service';

export interface MfaStepUpParams {
  externalUserId: string;
  entraTenantId: string;
  phoneFieldName: string;
  selectedPhoneNumber: string;
  authContextId?: AuthContextId;
}

export interface MfaEmailStepUpParams {
  externalUserId: string;
  entraTenantId: string;
  emailFieldName: string;
  authContextId?: AuthContextId;
}

export interface MfaExistingPhoneStepUpParams {
  externalUserId: string;
  entraTenantId: string;
  selectedPhoneNumber: string;
  authContextId?: AuthContextId;
}

export interface MfaExistingEmailStepUpParams {
  externalUserId: string;
  entraTenantId: string;
  selectedEmailAddress: string;
  authContextId?: AuthContextId;
}

export interface MfaPhoneStepUpOption {
  phoneFieldName: string;
  phoneNumber: string;
}

export interface MfaStepUpMethodOption {
  value: string;
  label: string;
  masked: string;
}

export interface MfaStepUpMethodSet {
  phones: MfaStepUpMethodOption[];
  emails: MfaStepUpMethodOption[];
  emailFieldNameFor(value: string): string | undefined;
  phoneFieldNameFor(value: string): string | undefined;
  isEntraOnlyPhone(value: string): boolean;
  challengeEmail(value: string, authContextId?: AuthContextId): Promise<CiamCallbackResult | null>;
  challengePhone(value: string, authContextId?: AuthContextId): Promise<CiamCallbackResult | null>;
}

interface MfaPropertyEntry {
  fieldName: string;
  value: string;
}

const MFA_EMAIL_FIELD_ORDER = [
  'MFAEmailPrimary',
  'MFAEmailAlternate',
  'MFAEmailTertiary',
  'MFAEmailQuaternary',
  'MFAEmailQuintenary',
];

const MFA_PHONE_FIELD_ORDER = [
  'MFAPhonePrimary',
  'MFAPhoneAlternate',
  'MFAPhoneTertiary',
  'MFAPhoneQuaternary',
  'MFAPhoneQuintenary',
];

const MFA_PHONE_FIELD_NAMES: Record<keyof CustomerIdentityMfaPhones, string> = {
  primary: 'MFAPhonePrimary',
  alternate: 'MFAPhoneAlternate',
  tertiary: 'MFAPhoneTertiary',
  quaternary: 'MFAPhoneQuaternary',
  quintenary: 'MFAPhoneQuintenary',
};

export class MfaPhoneVerificationError extends Error {
  constructor(message = 'The selected phone number could not be verified in Microsoft Entra.') {
    super(message);
    this.name = 'MfaPhoneVerificationError';
  }
}

export class MfaEmailVerificationError extends Error {
  constructor(message = 'The selected email address could not be verified in Microsoft Entra.') {
    super(message);
    this.name = 'MfaEmailVerificationError';
  }
}

export class MfaMethodResolutionError extends Error {
  constructor(message = 'The selected verification method could not be resolved to a stored MFA field.') {
    super(message);
    this.name = 'MfaMethodResolutionError';
  }
}

@Injectable({ providedIn: 'root' })
export class MfaStepUpService {
  private readonly customerIdentityService = inject(CustomerIdentityService);
  private readonly ciamAuthService = inject(CiamAuthService);
  private readonly loggingService = inject(LoggingService);

  private static readonly maxVerificationAttempts = 5;
  private static readonly verificationDelayMs = 2000;

  async getMfaPhoneOptions(entraTenantId: string, email: string): Promise<MfaPhoneStepUpOption[]> {
    const lookup = await this.customerIdentityService.legacyUserLookup(entraTenantId, email);
    return this.mapMfaPhones(lookup.mfaPhones);
  }

  private mapMfaPhones(mfaPhones?: CustomerIdentityMfaPhones): MfaPhoneStepUpOption[] {
    if (!mfaPhones) {
      return [];
    }

    return (Object.keys(MFA_PHONE_FIELD_NAMES) as (keyof CustomerIdentityMfaPhones)[])
      .map((key) => ({ phoneFieldName: MFA_PHONE_FIELD_NAMES[key], phoneNumber: mfaPhones[key] }))
      .filter((option): option is MfaPhoneStepUpOption => !!option.phoneNumber);
  }

  async buildStepUpMethods(externalUserId: string, entraTenantId: string): Promise<MfaStepUpMethodSet> {
    const [propertiesResult, currentMethods] = await Promise.all([
      this.customerIdentityService.getUserProperties(externalUserId, 'MFA'),
      this.customerIdentityService.getAuthenticationMethods(externalUserId, entraTenantId).catch((error) => {
        this.loggingService.logWarning(`[MfaStepUpService] Failed to load current auth methods: ${error}`);
        return { methods: [] };
      }),
    ]);

    const parsed = this.parseMfaProperties(propertiesResult.propertiesJson);

    const emailFieldNameByValue = new Map<string, string>();
    const phoneFieldNameByValue = new Map<string, string>();
    const entraOnlyPhoneValues = new Set<string>();

    const emails = this.mapEmailEntries(parsed.emails, emailFieldNameByValue);
    const ciPhones = this.mapPhoneEntries(parsed.phones, phoneFieldNameByValue);
    const phones = this.mergeEntraPhoneOptions(ciPhones, currentMethods.methods, entraOnlyPhoneValues);

    return {
      phones,
      emails,
      emailFieldNameFor: (value) => emailFieldNameByValue.get(value),
      phoneFieldNameFor: (value) => phoneFieldNameByValue.get(value),
      isEntraOnlyPhone: (value) => entraOnlyPhoneValues.has(value),
      challengeEmail: (value, authContextId = AUTH_CONTEXT.SENSITIVE_OPERATION) => {
        const emailFieldName = emailFieldNameByValue.get(value);
        if (!emailFieldName) {
          return Promise.reject(new MfaMethodResolutionError());
        }
        return this.registerEmailAndChallenge({ externalUserId, entraTenantId, emailFieldName, authContextId });
      },
      challengePhone: (value, authContextId = AUTH_CONTEXT.SENSITIVE_OPERATION) => {
        const phoneFieldName = phoneFieldNameByValue.get(value);
        if (phoneFieldName) {
          return this.registerVerifyAndChallenge({
            externalUserId,
            entraTenantId,
            phoneFieldName,
            selectedPhoneNumber: value,
            authContextId,
          });
        }
        if (entraOnlyPhoneValues.has(value)) {
          return this.challengeExistingPhone({ externalUserId, entraTenantId, selectedPhoneNumber: value, authContextId });
        }
        return Promise.reject(new MfaMethodResolutionError());
      },
    };
  }

  private parseMfaProperties(propertiesJson: string): { emails: MfaPropertyEntry[]; phones: MfaPropertyEntry[] } {
    const emails: MfaPropertyEntry[] = [];
    const phones: MfaPropertyEntry[] = [];

    for (const instance of this.extractMfaInstances(propertiesJson)) {
      for (const [fieldName, rawValue] of Object.entries(instance)) {
        if (fieldName === 'groupInstanceId' || typeof rawValue !== 'string') continue;

        const value = rawValue.trim();
        if (!value) continue;

        if (fieldName.startsWith('MFAEmail')) {
          emails.push({ fieldName, value });
        } else if (fieldName.startsWith('MFAPhone')) {
          phones.push({ fieldName, value });
        }
      }
    }

    return {
      emails: this.sortByFieldOrder(emails, MFA_EMAIL_FIELD_ORDER),
      phones: this.sortByFieldOrder(phones, MFA_PHONE_FIELD_ORDER),
    };
  }

  private extractMfaInstances(propertiesJson: string): Record<string, unknown>[] {
    let parsed: unknown;
    try {
      parsed = JSON.parse(propertiesJson);
    } catch {
      return [];
    }

    if (!this.isRecord(parsed)) return [];

    const groups = parsed['groups'];
    if (!this.isRecord(groups)) return [];

    const mfa = groups['MFA'];
    if (!Array.isArray(mfa)) return [];

    return mfa.filter((instance): instance is Record<string, unknown> => this.isRecord(instance));
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private sortByFieldOrder(entries: MfaPropertyEntry[], fieldOrder: string[]): MfaPropertyEntry[] {
    const orderIndex = (fieldName: string): number => {
      const index = fieldOrder.indexOf(fieldName);
      return index === -1 ? fieldOrder.length : index;
    };
    return [...entries].sort((a, b) => orderIndex(a.fieldName) - orderIndex(b.fieldName));
  }

  private mapEmailEntries(entries: MfaPropertyEntry[], fieldNameByValue: Map<string, string>): MfaStepUpMethodOption[] {
    const options: MfaStepUpMethodOption[] = [];
    const seen = new Set<string>();

    for (const entry of entries) {
      const key = entry.value.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);

      fieldNameByValue.set(entry.value, entry.fieldName);
      options.push({ value: entry.value, label: entry.value, masked: entry.value });
    }

    return options;
  }

  private mapPhoneEntries(entries: MfaPropertyEntry[], fieldNameByValue: Map<string, string>): MfaStepUpMethodOption[] {
    const options: MfaStepUpMethodOption[] = [];
    const seen = new Set<string>();

    for (const entry of entries) {
      const digits = this.normalizePhone(entry.value);
      if (!digits || seen.has(digits)) continue;
      seen.add(digits);

      fieldNameByValue.set(entry.value, entry.fieldName);
      options.push({
        value: entry.value,
        label: this.maskPhoneNumber(entry.value),
        masked: this.maskPhoneNumber(entry.value),
      });
    }

    return options;
  }

  private mergeEntraPhoneOptions(
    ciPhones: MfaStepUpMethodOption[],
    entraMethods: CustomerIdentityAuthenticationMethod[],
    entraOnlyPhoneValues: Set<string>,
  ): MfaStepUpMethodOption[] {
    const seenDigits = new Set(ciPhones.map((phone) => this.normalizePhone(phone.value)));
    const entraOnlyPhones: MfaStepUpMethodOption[] = [];

    for (const method of entraMethods) {
      if (!method.phoneNumber) continue;

      const digits = this.normalizePhone(method.phoneNumber);
      if (!digits || seenDigits.has(digits)) continue;

      seenDigits.add(digits);
      entraOnlyPhoneValues.add(method.phoneNumber);
      entraOnlyPhones.push({
        value: method.phoneNumber,
        label: this.maskPhoneNumber(method.phoneNumber),
        masked: this.maskPhoneNumber(method.phoneNumber),
      });
    }

    return [...ciPhones, ...entraOnlyPhones];
  }

  private maskPhoneNumber(phone: string): string {
    const digits = this.normalizePhone(phone);
    const lastFour = digits.slice(-4);
    return `***-***-${lastFour}`;
  }

  async stepUpWithCurrentMethods(
    authContextId: AuthContextId = AUTH_CONTEXT.SENSITIVE_OPERATION,
  ): Promise<CiamCallbackResult | null> {
    return this.ciamAuthService.requestStepUpMfa(authContextId);
  }

  async registerVerifyAndChallenge(params: MfaStepUpParams): Promise<CiamCallbackResult | null> {
    const authContextId = params.authContextId ?? AUTH_CONTEXT.SENSITIVE_OPERATION;

    await this.customerIdentityService.createPhoneAuthMethod(
      params.externalUserId,
      params.phoneFieldName,
      'mobile',
      params.entraTenantId,
    );

    const verified = await this.verifyExactPhone(
      params.externalUserId,
      params.entraTenantId,
      params.selectedPhoneNumber,
    );

    if (!verified) {
      throw new MfaPhoneVerificationError();
    }

    return this.ciamAuthService.requestStepUpMfa(authContextId);
  }

  async challengeExistingPhone(params: MfaExistingPhoneStepUpParams): Promise<CiamCallbackResult | null> {
    const authContextId = params.authContextId ?? AUTH_CONTEXT.SENSITIVE_OPERATION;

    const verified = await this.verifyExactPhone(
      params.externalUserId,
      params.entraTenantId,
      params.selectedPhoneNumber,
    );

    if (!verified) {
      throw new MfaPhoneVerificationError();
    }

    return this.ciamAuthService.requestStepUpMfa(authContextId);
  }

  async challengeExistingEmail(params: MfaExistingEmailStepUpParams): Promise<CiamCallbackResult | null> {
    const authContextId = params.authContextId ?? AUTH_CONTEXT.SENSITIVE_OPERATION;

    const verified = await this.verifyExactEmail(
      params.externalUserId,
      params.entraTenantId,
      params.selectedEmailAddress,
    );

    if (!verified) {
      throw new MfaEmailVerificationError();
    }

    return this.ciamAuthService.requestStepUpMfa(authContextId);
  }

  async registerEmailAndChallenge(params: MfaEmailStepUpParams): Promise<CiamCallbackResult | null> {
    const authContextId = params.authContextId ?? AUTH_CONTEXT.SENSITIVE_OPERATION;

    await this.customerIdentityService.createEmailAuthMethod(
      params.externalUserId,
      params.emailFieldName,
      params.entraTenantId,
    );

    return this.ciamAuthService.requestStepUpMfa(authContextId);
  }

  async verifyExactPhone(
    externalUserId: string,
    entraTenantId: string,
    selectedPhoneNumber: string,
  ): Promise<boolean> {
    const expected = this.normalizePhone(selectedPhoneNumber);

    for (let attempt = 1; attempt <= MfaStepUpService.maxVerificationAttempts; attempt++) {
      try {
        const authMethods = await this.customerIdentityService.getAuthenticationMethods(
          externalUserId,
          entraTenantId,
        );

        const phoneMethods = authMethods.methods.filter(
          (m) => !!m.phoneNumber && m.odataType.toLowerCase().includes('phone'),
        );

        const matched = phoneMethods.some(
          (m) => !!m.phoneNumber && this.normalizePhone(m.phoneNumber) === expected,
        );

        if (matched) {
          this.loggingService.logInfo(
            `[MfaStepUpService] Entra phone verified on attempt ${attempt}`,
          );
          return true;
        }

        this.loggingService.logInfo(
          `[MfaStepUpService] Entra phone not yet matching selected number (attempt ${attempt}/${MfaStepUpService.maxVerificationAttempts})`,
        );
      } catch (error) {
        this.loggingService.logWarning(
          `[MfaStepUpService] Failed to read authentication methods (attempt ${attempt}/${MfaStepUpService.maxVerificationAttempts}): ${error}`,
        );
      }

      if (attempt < MfaStepUpService.maxVerificationAttempts) {
        await new Promise((resolve) => setTimeout(resolve, MfaStepUpService.verificationDelayMs));
      }
    }

    return false;
  }

  async verifyExactEmail(
    externalUserId: string,
    entraTenantId: string,
    selectedEmailAddress: string,
  ): Promise<boolean> {
    const expected = selectedEmailAddress.trim().toLowerCase();

    for (let attempt = 1; attempt <= MfaStepUpService.maxVerificationAttempts; attempt++) {
      try {
        const authMethods = await this.customerIdentityService.getAuthenticationMethods(
          externalUserId,
          entraTenantId,
        );

        const emailMethods = authMethods.methods.filter(
          (m) => !!m.emailAddress && m.odataType.toLowerCase().includes('email'),
        );

        const matched = emailMethods.some(
          (m) => !!m.emailAddress && m.emailAddress.trim().toLowerCase() === expected,
        );

        if (matched) {
          this.loggingService.logInfo(
            `[MfaStepUpService] Entra email verified on attempt ${attempt}`,
          );
          return true;
        }

        this.loggingService.logInfo(
          `[MfaStepUpService] Entra email not yet matching selected address (attempt ${attempt}/${MfaStepUpService.maxVerificationAttempts})`,
        );
      } catch (error) {
        this.loggingService.logWarning(
          `[MfaStepUpService] Failed to read authentication methods (attempt ${attempt}/${MfaStepUpService.maxVerificationAttempts}): ${error}`,
        );
      }

      if (attempt < MfaStepUpService.maxVerificationAttempts) {
        await new Promise((resolve) => setTimeout(resolve, MfaStepUpService.verificationDelayMs));
      }
    }

    return false;
  }

  private normalizePhone(phone: string): string {
    const digits = phone.replace(/\D/g, '');
    return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  }
}
